import { useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';

const DRAG_THRESHOLD_PX = 5;

export function useDragToScroll<T extends HTMLElement>() {
  const suppressClick = useRef(false);
  const stopDrag = useRef<(() => void) | null>(null);

  useEffect(() => () => stopDrag.current?.(), []);

  return {
    onMouseDown: (event: MouseEvent<T>) => {
      if (event.button !== 0) return;
      stopDrag.current?.();

      const element = event.currentTarget;
      const startX = event.clientX;
      const startScroll = element.scrollLeft;
      let moved = false;
      suppressClick.current = false;

      const onMove = (move: globalThis.MouseEvent) => {
        const distance = move.clientX - startX;
        if (!moved && Math.abs(distance) > DRAG_THRESHOLD_PX) {
          moved = true;
          element.dataset.dragging = 'true';
        }
        if (moved) element.scrollLeft = startScroll - distance;
      };

      const stop = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('blur', stop);
        delete element.dataset.dragging;
        stopDrag.current = null;
      };

      const onUp = () => {
        suppressClick.current = moved;
        stop();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('blur', stop);
      stopDrag.current = stop;
    },
    onClickCapture: (event: MouseEvent<T>) => {
      if (!suppressClick.current || event.detail === 0) return;
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
